import { useState } from "react";
import { request } from "../../../utils";
import { Modal } from ".";
import Alert from "../alert/Alert";
import Button from "../button/Button";
import { autoClearError, validatePinChange } from "../../../utils/helpers";
import PinInput from "../../form/input/PinInput";

interface ChangePinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ChangePinModal({
  isOpen,
  onClose,
  onSuccess,
}: ChangePinModalProps) {
  const [oldPin, setOldPin] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setOldPin("");
    setPin("");
    setConfirmPin("");
    setError("");
    setSuccess(false);
    setLoading(false);
  };

  const handleCloseModal = () => {
    resetForm();
    onClose();
  };

  const handleChangePin = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validatePinChange({
      oldPin,
      pin,
      confirmPin,
    });

    if (validationError) {
      setError(validationError);
      autoClearError(setError);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      await request.patch({
        url: "/user/pin",
        data: { oldPin, pin, confirmPin },
      });

      setSuccess(true);
      onSuccess();
    } catch (err) {
      setError((err as Error).message || "Failed to change PIN.");
      autoClearError(setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} className="max-w-md p-6">
      <form onSubmit={handleChangePin} className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
          Change Transaction PIN
        </h2>

        {error && <Alert variant="error" title="Error" message={error} />}

        {success && (
          <Alert
            variant="success"
            title="Success"
            message="PIN changed successfully."
          />
        )}

        <PinInput label="Old PIN" value={oldPin} onChange={setOldPin} />
        <PinInput label="New PIN" value={pin} onChange={setPin} />
        <PinInput
          label="Confirm PIN"
          value={confirmPin}
          onChange={setConfirmPin}
        />

        <div className="flex items-center gap-3 justify-end">
          <Button size="sm" variant="outline" onClick={handleCloseModal}>
            Close
          </Button>
          <Button
            size="sm"
            type="submit"
            disabled={
              loading ||
              !oldPin ||
              !pin ||
              !confirmPin ||
              oldPin.length < 4 ||
              pin.length < 4 ||
              confirmPin.length < 4
            }
          >
            {loading ? "Updating..." : "Update PIN"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}